import { useEffect, useRef } from "react";
import styled from "styled-components";
import type { ChatMessage } from "../../hooks/usePortfolioChat";

type Props = {
  messages: ChatMessage[];
  isStreaming: boolean;
};

const ChatMessages = ({ messages, isStreaming }: Props) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  if (messages.length === 0) {
    return (
      <EmptyState>
        <p>Ask me about Mark&apos;s projects, experience, skills, or how to get in touch.</p>
      </EmptyState>
    );
  }

  return (
    <Root>
      {messages.map((message) => (
        <Bubble key={message.id} $role={message.role}>
          {message.content}
          {message.role === "assistant" &&
            isStreaming &&
            message === messages[messages.length - 1] &&
            !message.content && <Cursor aria-hidden>|</Cursor>}
        </Bubble>
      ))}
      <div ref={bottomRef} />
    </Root>
  );
};

export default ChatMessages;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0.15rem;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.5;
  padding: 1rem;
`;

const Bubble = styled.div<{ $role: "user" | "assistant" }>`
  align-self: ${({ $role }) => ($role === "user" ? "flex-end" : "flex-start")};
  max-width: 88%;
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  font-size: 0.88rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: ${({ $role }) =>
    $role === "user" ? "var(--secondary-color)" : "var(--surface-elevated)"};
  color: var(--white-dart);
  border: 1px solid
    ${({ $role }) =>
      $role === "user" ? "var(--border-accent)" : "var(--border-subtle)"};
`;

const Cursor = styled.span`
  display: inline-block;
  margin-left: 2px;
  color: var(--green);
  animation: blink 1s step-end infinite;

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;
